import React, { useEffect, useMemo, useState } from 'react';

import FeaturedArticle from '@components/FeedItems/FeaturedArticle';
import Spinner from '@components/Spinner';
import type { Article } from '@interfaces/homepage';
import { getUserRecommendedArticles } from '@utils/backend-api/articles';

import MyTISection from './Section';
import { MY_TI_ENGAGEMENT_LABELS, MY_TI_SECTION_IDS } from './constants';
import useRegisterSection from './useRegisterSection';

const MyTIRecommendedArticles = ({ initialCurrentUser }) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const recommendedArticlesDisplayText = useMemo(() => {
    if (!initialCurrentUser) return MY_TI_ENGAGEMENT_LABELS.Engaged.NonPro.RecommendedArticles;
    const isPro = initialCurrentUser?.is_pro_subscriber;

    return isPro
      ? MY_TI_ENGAGEMENT_LABELS.Engaged.Pro.RecommendedArticles
      : MY_TI_ENGAGEMENT_LABELS.Engaged.NonPro.RecommendedArticles;
  }, [initialCurrentUser]);

  useEffect(() => {
    let cancelled = false;

    const fetchArticles = async () => {
      try {
        const results = await getUserRecommendedArticles();
        if (!cancelled) setArticles(results);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchArticles();

    return () => {
      cancelled = true;
    };
  }, []);

  useRegisterSection(MY_TI_SECTION_IDS.RecommendedArticles, isLoading || articles.length > 0);

  if (!isLoading && articles.length === 0) return null;

  return (
    <MyTISection
      id={MY_TI_SECTION_IDS.RecommendedArticles}
      highlight={recommendedArticlesDisplayText.label}
      subtitle={recommendedArticlesDisplayText.subtitle}
      title={recommendedArticlesDisplayText.title}
    >
      {isLoading ? (
        <Spinner marginY="small" />
      ) : (
        <div className="mt-32 flex flex-col gap-32 md:flex-row md:gap-112">
          {articles.map((article) => (
            <FeaturedArticle
              key={article.id}
              className="!border-0 !pb-0 [&_h2]:text-headline-sm"
              featuredArticle={article}
              isFluid
            />
          ))}
        </div>
      )}
    </MyTISection>
  );
};

export default MyTIRecommendedArticles;
